import { useState, useEffect } from 'react';
import useSelectionStore from '@store/useSelectionStore';
import { ClickableType } from '@utils/enums';
import Tabs from '@components/ui/Tabs/Tabs';
import UnitInfoPanel from '../UnitInfoPanel/UnitInfoPanel';
import StructureInfoPanel from '../StructureInfoPanel/StructureInfoPanel';
import ActionsPanel from '../ActionsPanel/ActionsPanel';
import AllActionsPanel from '../AllActionsPanel/AllActionsPanel';
import { Info, ListOrdered } from 'lucide-react';
import './InfoPanel.css';

const TAB_INFO = 'info';
const TAB_ACTIONS = 'actions';

export default function InfoPanel() {
  const { currentClick } = useSelectionStore();
  const [activeTab, setActiveTab] = useState(TAB_INFO);
  const [highlightedActionId, setHighlightedActionId] = useState(null);

  // Switch tabs based on what was clicked
  useEffect(() => {
    if (!currentClick) return;

    if (currentClick.type === ClickableType.PLANNED_ACTION) {
      // Planned action clicked -> switch to Actions tab and highlight
      setActiveTab(TAB_ACTIONS);

      // Get the first action ID from the click data to highlight
      const actions = currentClick.data;
      if (actions && actions.length > 0) {
        setHighlightedActionId(actions[0].id);
      }
    } else if (currentClick.type === ClickableType.UNIT ||
               currentClick.type === ClickableType.DEPLOYABLE ||
               currentClick.type === ClickableType.STRUCTURE) {
      // Unit, deployable or structure clicked -> switch to Info tab
      setActiveTab(TAB_INFO);
    }
  }, [currentClick]);

  const handleHighlightClear = () => {
    setHighlightedActionId(null);
  };

  // Content for the Info tab
  const infoContent = (
    <div className="info-panel-content">
      {!currentClick && (
        <div className="info-panel-empty-content">
          <p>No selection</p>
        </div>
      )}

      {/* Unit Info Panel - works for both deployed units and deployable units */}
      {currentClick && (currentClick.type === ClickableType.UNIT || currentClick.type === ClickableType.DEPLOYABLE) && (
        <>
          <UnitInfoPanel />
          {/* Actions Panel - only for deployed units, not deployable */}
          {currentClick.type === ClickableType.UNIT && (
            <ActionsPanel />
          )}
        </>
      )}

      {/* Structure Info Panel */}
      {currentClick && currentClick.type === ClickableType.STRUCTURE && (
        <StructureInfoPanel />
      )}

      {/* When planned action is clicked, show message to check Actions tab */}
      {currentClick && currentClick.type === ClickableType.PLANNED_ACTION && (
        <div className="info-panel-empty-content">
          <p>See Actions tab</p>
        </div>
      )}
    </div>
  );

  // Content for the Actions tab
  const actionsContent = (
    <AllActionsPanel
      highlightedActionId={highlightedActionId}
      onHighlightClear={handleHighlightClear}
    />
  );

  const tabs = [
    {
      id: TAB_INFO,
      label: 'Info',
      icon: <Info size={14} />,
      content: infoContent
    },
    {
      id: TAB_ACTIONS,
      label: 'Actions',
      icon: <ListOrdered size={14} />,
      content: actionsContent
    }
  ];

  return (
    <div className="info-panel panel">
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </div>
  );
}